import { FC } from "react";
import { BrowserRouter as Router, Routes } from "react-router-dom";
import * as Sentry from "@sentry/react";

import { useAuthContext } from "providers/Auth";
import { Loader } from "shared/components/Loader";
import { Show } from "shared/components/helpers/Show";
import { MessagePopup } from "shared/app/MessagePopup";
import { BoomHelpPanel } from "shared/app/BoomHelp";
import { useBoomHelpEnabled } from "shared/app/BoomHelp/use-boom-help-enabled";

import { renderRoutes } from "./Routes/render-routes";
import { getPublicRoutes } from "./Routes/public/routes";
import { getUnauthRoutes } from "./Routes/unauth/routes";
import { getOnboardingRoutes } from "./Routes/onboarding/routes";
import { getActiveRoutes } from "./Routes/active/routes";

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

export const RouterProvider: FC = () => {
  const { auth, permissions, type } = useAuthContext();
  const boomHelpEnabled = useBoomHelpEnabled();

  if (auth === undefined)
    return (
      <div className="animate-appear pt-10 flex flex-col items-center">
        <Loader />
      </div>
    );

  const publicRoutes = getPublicRoutes();

  const getAuthRoutes = () => {
    if (!auth) return getUnauthRoutes();
    if (auth.partner.status === "active")
      return getActiveRoutes(type, permissions);
    return getOnboardingRoutes();
  };

  return (
    <Router>
      <SentryRoutes>
        {renderRoutes([...publicRoutes, ...getAuthRoutes()])}
      </SentryRoutes>
      {/* One message pop-up for the whole app (CRM-1133). A sibling of the
          routes, so navigating never unmounts an open conversation, and inside
          <Router>, so "Go to inbox" can navigate in place — the softphone
          overlay is mounted in App.tsx, outside the Router, and has to open
          links in a new tab for exactly that reason. */}
      <Show if={auth?.partner.status === "active"}>
        <MessagePopup />
      </Show>
      <Show if={auth?.partner.status === "active" && boomHelpEnabled}>
        <BoomHelpPanel />
      </Show>
    </Router>
  );
};
